/**
 * Browser adapter for browser API operations.
 * Enables deterministic testing by allowing browser APIs to be controlled in tests.
 */

import type { StorageAdapter } from './storage'

/**
 * Location-like object returned by getLocation().
 */
export interface LocationLike {
    origin: string
    pathname: string
    href: string
}

/**
 * Interface for browser adapter operations.
 */
export interface BrowserAdapter {
    /**
     * Match a media query string.
     * @param query - The media query string (e.g., '(prefers-reduced-motion: reduce)')
     * @returns A MediaQueryList object
     */
    matchMedia(query: string): MediaQueryList

    /**
     * Set the document language attribute.
     * @param lang - The language code (e.g., 'en', 'de')
     */
    setDocumentLang(lang: string): void

    /**
     * Add an event listener to the window.
     * @param type - The event type (e.g., 'llm-key-changed')
     * @param handler - The event handler function
     */
    addEventListener(type: string, handler: EventListener): void

    /**
     * Remove an event listener from the window.
     * @param type - The event type
     * @param handler - The event handler function
     */
    removeEventListener(type: string, handler: EventListener): void

    /**
     * Set the document title.
     * @param title - The page title
     */
    setDocumentTitle(title: string): void

    /**
     * Update a meta tag in the document head.
     * Creates the tag if it doesn't exist.
     * @param name - The meta tag name or property (e.g., 'description' or 'og:title')
     * @param content - The content value
     * @param attribute - The attribute name to match on ('name' or 'property', default: 'name')
     */
    updateMetaTag(name: string, content: string, attribute?: 'name' | 'property'): void

    /**
     * Update a link tag in the document head.
     * Creates the tag if it doesn't exist.
     * @param rel - The rel attribute value (e.g., 'canonical')
     * @param href - The href value
     */
    updateLinkTag(rel: string, href: string): void

    /**
     * Get the current location (origin, pathname, href).
     * @returns Location-like object
     */
    getLocation(): LocationLike

    /**
     * Get an element by ID.
     * @param id - The element ID
     * @returns The element or null if not found
     */
    getElementById(id: string): HTMLElement | null

    /**
     * Get the currently focused element.
     * @returns The active element or null
     */
    getActiveElement(): HTMLElement | null

    /**
     * Dispatch a custom event on the window.
     * @param event - The event to dispatch
     */
    dispatchEvent(event: Event): void

    /**
     * Access to the global document object.
     */
    readonly document?: Document

    /**
     * Access to localStorage.
     */
    localStorage: StorageAdapter

    /**
     * Access to sessionStorage.
     */
    sessionStorage: StorageAdapter

    /**
     * Create a BroadcastChannel instance.
     * @param name - The channel name
     * @returns A BroadcastChannel instance or null if not supported
     */
    createBroadcastChannel(name: string): BroadcastChannel | null

    /**
     * Access to window object (for properties like window.location, etc.).
     * Note: This is a minimal interface - only add what's actually needed.
     */
    window: {
        /**
         * Dispatch a custom event on the window.
         * @param event - The event to dispatch
         */
        dispatchEvent(event: Event): void
    }

    /**
     * Set a timer that repeatedly calls a function.
     * @param callback - The function to call
     * @param ms - The interval in milliseconds
     * @returns A timer ID that can be passed to clearInterval
     */
    setInterval(callback: () => void, ms: number): number

    /**
     * Clear a timer set by setInterval.
     * @param id - The timer ID returned by setInterval
     */
    clearInterval(id: number): void

    /**
     * Set a one-time timer.
     * @param callback - The function to call
     * @param ms - The delay in milliseconds
     * @returns A timer ID that can be passed to clearTimeout
     */
    setTimeout(callback: () => void, ms: number): number

    /**
     * Clear a timer set by setTimeout.
     * @param id - The timer ID returned by setTimeout
     */
    clearTimeout(id: number): void

    /**
     * Clipboard API operations.
     */
    clipboard: {
        /**
         * Write text to the clipboard.
         * @param text - The text to write
         * @returns A promise that resolves when the text is written
         */
        writeText(text: string): Promise<void>

        /**
         * Read text from the clipboard.
         * @returns A promise that resolves with the clipboard text
         */
        readText(): Promise<string>
    }

    /**
     * URL API operations.
     */
    url: {
        /**
         * Create an object URL from a blob.
         * @param blob - The blob to create a URL for
         * @returns The object URL string
         */
        createObjectURL(blob: Blob): string

        /**
         * Revoke an object URL.
         * @param url - The object URL to revoke
         */
        revokeObjectURL(url: string): void
    }

    /**
     * Speech recognition operations (Web Speech API).
     */
    speechRecognition: {
        /**
         * Check if speech recognition is supported in the current browser.
         * @returns True if SpeechRecognition or webkitSpeechRecognition is available
         */
        isSupported(): boolean

        /**
         * Create a SpeechRecognition instance configured for the given language.
         * @param lang - The language code (e.g., 'en')
         * @returns A configured SpeechRecognition instance or null if not supported
         */
        create(lang: string): SpeechRecognition | null
    }
}

/**
 * Browser adapter implementation using window/document APIs.
 * Use this in production code.
 */
export const browserAdapter: BrowserAdapter = {
    matchMedia: (query: string) => {
        if (typeof window !== 'undefined' && window.matchMedia) {
            return window.matchMedia(query)
        }
        // Fallback for SSR/test environments
        return {
            matches: false,
            media: query,
            onchange: null,
            addListener: () => {},
            removeListener: () => {},
            addEventListener: () => {},
            removeEventListener: () => {},
            dispatchEvent: () => false,
        } as MediaQueryList
    },
    setDocumentLang: (lang: string) => {
        if (typeof document !== 'undefined') {
            document.documentElement.lang = lang
        }
    },
    addEventListener: (type: string, handler: EventListener) => {
        if (typeof window !== 'undefined') {
            window.addEventListener(type, handler)
        }
    },
    removeEventListener: (type: string, handler: EventListener) => {
        if (typeof window !== 'undefined') {
            window.removeEventListener(type, handler)
        }
    },
    setDocumentTitle: (title: string) => {
        if (typeof document !== 'undefined') {
            document.title = title
        }
    },
    updateMetaTag: (name: string, content: string, attribute: 'name' | 'property' = 'name') => {
        if (typeof document === 'undefined') return

        const selector =
            attribute === 'property' ? `meta[property="${name}"]` : `meta[name="${name}"]`

        let metaTag = document.querySelector(selector)
        if (!metaTag) {
            metaTag = document.createElement('meta')
            if (attribute === 'property') {
                metaTag.setAttribute('property', name)
            } else {
                metaTag.setAttribute('name', name)
            }
            document.head.appendChild(metaTag)
        }
        metaTag.setAttribute('content', content)
    },
    updateLinkTag: (rel: string, href: string) => {
        if (typeof document === 'undefined') return

        let linkTag = document.querySelector(`link[rel="${rel}"]`)
        if (!linkTag) {
            linkTag = document.createElement('link')
            linkTag.setAttribute('rel', rel)
            document.head.appendChild(linkTag)
        }
        linkTag.setAttribute('href', href)
    },
    getLocation: (): LocationLike => {
        if (typeof window !== 'undefined' && window.location) {
            return {
                origin: window.location.origin,
                pathname: window.location.pathname,
                href: window.location.href,
            }
        }
        // Fallback for SSR/test environments
        return {
            origin: '',
            pathname: '/',
            href: '/',
        }
    },
    getElementById: (id: string): HTMLElement | null => {
        if (typeof document !== 'undefined') {
            return document.getElementById(id)
        }
        return null
    },
    getActiveElement: (): HTMLElement | null => {
        if (typeof document !== 'undefined' && document.activeElement) {
            return document.activeElement as HTMLElement
        }
        return null
    },
    dispatchEvent: (event: Event): void => {
        if (typeof window !== 'undefined') {
            window.dispatchEvent(event)
        }
    },
    document: typeof document !== 'undefined' ? document : undefined,
    localStorage: {
        getItem: (key: string): string | null => {
            if (typeof localStorage === 'undefined') return null
            return localStorage.getItem(key)
        },
        setItem: (key: string, value: string): void => {
            if (typeof localStorage === 'undefined') return
            localStorage.setItem(key, value)
        },
        removeItem: (key: string): void => {
            if (typeof localStorage === 'undefined') return
            localStorage.removeItem(key)
        },
        clear: (): void => {
            if (typeof localStorage === 'undefined') return
            localStorage.clear()
        },
    },
    sessionStorage: {
        getItem: (key: string): string | null => {
            if (typeof sessionStorage === 'undefined') return null
            return sessionStorage.getItem(key)
        },
        setItem: (key: string, value: string): void => {
            if (typeof sessionStorage === 'undefined') return
            sessionStorage.setItem(key, value)
        },
        removeItem: (key: string): void => {
            if (typeof sessionStorage === 'undefined') return
            sessionStorage.removeItem(key)
        },
        clear: (): void => {
            if (typeof sessionStorage === 'undefined') return
            sessionStorage.clear()
        },
    },
    createBroadcastChannel: (name: string): BroadcastChannel | null => {
        if (typeof BroadcastChannel === 'undefined') return null
        try {
            return new BroadcastChannel(name)
        } catch {
            return null
        }
    },
    window: {
        dispatchEvent: (event: Event): void => {
            if (typeof window !== 'undefined') {
                window.dispatchEvent(event)
            }
        },
    },
    setInterval: (callback: () => void, ms: number): number => {
        if (typeof window !== 'undefined') {
            return window.setInterval(callback, ms)
        }
        return 0
    },
    clearInterval: (id: number): void => {
        if (typeof window !== 'undefined') {
            window.clearInterval(id)
        }
    },
    setTimeout: (callback: () => void, ms: number): number => {
        if (typeof window !== 'undefined') {
            return window.setTimeout(callback, ms)
        }
        return 0
    },
    clearTimeout: (id: number): void => {
        if (typeof window !== 'undefined') {
            window.clearTimeout(id)
        }
    },
    clipboard: {
        writeText: async (text: string): Promise<void> => {
            if (
                typeof navigator !== 'undefined' &&
                navigator.clipboard &&
                navigator.clipboard.writeText
            ) {
                await navigator.clipboard.writeText(text)
            } else {
                throw new Error('Clipboard API is not available')
            }
        },
        readText: async (): Promise<string> => {
            if (
                typeof navigator !== 'undefined' &&
                navigator.clipboard &&
                navigator.clipboard.readText
            ) {
                return await navigator.clipboard.readText()
            } else {
                throw new Error('Clipboard API is not available')
            }
        },
    },
    url: {
        createObjectURL: (blob: Blob): string => {
            if (typeof URL !== 'undefined' && URL.createObjectURL) {
                return URL.createObjectURL(blob)
            } else {
                throw new Error('URL.createObjectURL is not available')
            }
        },
        revokeObjectURL: (url: string): void => {
            if (typeof URL !== 'undefined' && URL.revokeObjectURL) {
                URL.revokeObjectURL(url)
            }
        },
    },
    speechRecognition: {
        isSupported: (): boolean => {
            return (
                typeof window !== 'undefined' &&
                ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window)
            )
        },
        create: (lang: string): SpeechRecognition | null => {
            if (typeof window === 'undefined') return null
            const SpeechRecognitionCtor = window.SpeechRecognition || window.webkitSpeechRecognition
            if (!SpeechRecognitionCtor) return null
            const recognition = new SpeechRecognitionCtor()
            recognition.lang = lang
            recognition.continuous = true
            recognition.interimResults = true
            return recognition
        },
    },
}

/**
 * Injectable adapter instance (for testing).
 * Defaults to browserAdapter but can be overridden in tests.
 */
let adapterInstance: BrowserAdapter = browserAdapter

/**
 * Set the browser adapter instance.
 * Call in tests with a mock adapter.
 */
export const setBrowserAdapter = (adapter: BrowserAdapter): void => {
    adapterInstance = adapter
}

/**
 * Reset the browser adapter to the default browserAdapter.
 * Call in test cleanup.
 */
export const resetBrowserAdapter = (): void => {
    adapterInstance = browserAdapter
}

/**
 * Get the current browser adapter instance.
 * Use this instead of direct browser API calls.
 */
export const getBrowserAdapter = (): BrowserAdapter => adapterInstance
